import BaseballResultsProcessor from '../../entities/BaseballResultsProcessor.js';
import { 
    ResultData, ResultType,
    HitReprocessed, PitchReprocessed, PitchData,
} from "../../enums/index.js";
import PlateAppearanceFormatter from "./PlateAppearanceFormatter.js";

/**
 * This formatter is able to determine Plate Appearance for cumulative results for specific batter.
 * Unfortunately it is not so reliable like PlateAppearanceFormatter for session data, as it works mainly on date time for results without scoring info.
 */
class PlayerPlateAppearanceFormatter extends PlateAppearanceFormatter {
    /**
     * 
     * @param {undefined|number} forceNextPlateAppearanceThresholdS - optional, default 15 minutes, allows to customize threshold expressed in seconds after which next PA is forced
     */
    constructor(forceNextPlateAppearanceThresholdS) {
        super();

        if (typeof(forceNextPlateAppearanceThresholdS) === 'undefined') {
            // TODO - some of the KBO data has next PA in around 7-8 minutes, but for me it looks like an issue with the tagging
            this.forceNextPlateAppearanceThresholdS = 60 * 15;
        }
        else if (typeof(forceNextPlateAppearanceThresholdS) !== 'number' || forceNextPlateAppearanceThresholdS < 1) {
            throw new TypeError("forceNextPlateAppearanceThresholdS must contain number greater than 0");
        }
        else {
            this.forceNextPlateAppearanceThresholdS = forceNextPlateAppearanceThresholdS;
        }
    }

    format(results) {
        let paPerBatter = {};
        let previousBatterId = '';

        for (let i = 0; i < results.length; i++) {
            let result = results[i];

            if (!result || !result.hasOwnProperty(ResultData.Data.key) || !result[ResultData.Data.key]) {
                continue;
            }

            let uniquePlayerId = '';
            let isHit = false;

            // TODO - separate methods?
            switch (result[ResultData.Type.key]) {
                case ResultType.Pitch.key:
                    uniquePlayerId = this.determinePlayerIdFromPitch(result);
                    break;
                case ResultType.Hit.key:
                    isHit = true;
                    // check if there is related pitch...
                    let relatedPitch = BaseballResultsProcessor.getRelatedResult(result[ResultData.PitchId.key], results);
                    if (
                        relatedPitch && relatedPitch[ResultData.Data.key]
                        && relatedPitch[ResultData.Data.key][PitchReprocessed.PlateAppearanceR.key]
                        && relatedPitch[ResultData.Data.key][PitchReprocessed.PitchOfPlateAppearanceR.key]
                    ) {
                        result[ResultData.Data.key][HitReprocessed.PlateAppearanceR.key] = relatedPitch[ResultData.Data.key][PitchReprocessed.PlateAppearanceR.key];
                        result[ResultData.Data.key][HitReprocessed.PitchOfPlateAppearanceR.key] = relatedPitch[ResultData.Data.key][PitchReprocessed.PitchOfPlateAppearanceR.key];
                        continue;
                    }

                    // now we need to analyze and count it, as it looks like we have no pitch for this hit...
                    uniquePlayerId = this.determinePlayerIdFromHit(result);

                    break;
            
                default:
                    continue;
            }

            const propertyPA = isHit ? HitReprocessed.PlateAppearanceR.key : PitchReprocessed.PlateAppearanceR.key;
            const propertyPofPA = isHit ? HitReprocessed.PitchOfPlateAppearanceR.key : PitchReprocessed.PitchOfPlateAppearanceR.key;
            const resultCreatedOn = new Date(result[ResultData.CreateDate.key]);

            if (!(resultCreatedOn instanceof Date) || isNaN(resultCreatedOn)) {
                continue;
            }

            if (uniquePlayerId) {
                if (!paPerBatter.hasOwnProperty(uniquePlayerId)) {
                    paPerBatter[uniquePlayerId] = this.getDefaultPaCounter();
                }

                if (
                    !isHit 
                    && result[ResultData.Data.key].hasOwnProperty(PitchData.AfterPlayPlateAppearance.key)
                    && !isNaN(result[ResultData.Data.key][PitchData.AfterPlayPlateAppearance.key])
                    && result[ResultData.Data.key][PitchData.AfterPlayPlateAppearance.key] > 0
                ) {
                    // prefer scoring data
                    if (paPerBatter[uniquePlayerId].PA != result[ResultData.Data.key][PitchData.AfterPlayPlateAppearance.key]) {
                        paPerBatter[uniquePlayerId].PofPA = 0;
                    }
                    paPerBatter[uniquePlayerId].PA = result[ResultData.Data.key][PitchData.AfterPlayPlateAppearance.key];
                    paPerBatter[uniquePlayerId].LastResultOn = resultCreatedOn;
                }
                else if (
                    previousBatterId !== uniquePlayerId
                    || paPerBatter[uniquePlayerId].LastResultOn === null
                    || (resultCreatedOn.getTime() - paPerBatter[uniquePlayerId].LastResultOn.getTime()) / 1000 >= this.forceNextPlateAppearanceThresholdS
                ) {
                    paPerBatter[uniquePlayerId].PA ++;
                    paPerBatter[uniquePlayerId].PofPA = 0;
                    paPerBatter[uniquePlayerId].LastResultOn = resultCreatedOn;
                }

                paPerBatter[uniquePlayerId].PofPA ++;

                result[ResultData.Data.key][propertyPA] = paPerBatter[uniquePlayerId].PA;
                result[ResultData.Data.key][propertyPofPA] = paPerBatter[uniquePlayerId].PofPA;
            }
            else {
                result[ResultData.Data.key][propertyPA] = null;
                result[ResultData.Data.key][propertyPofPA] = null;
            }

            previousBatterId = uniquePlayerId;
        }

        return results;
    }

    getDefaultPaCounter() {
        return {
            PA: 0,
            PofPA: 0,
            LastResultOn: null,
        };
    }
};

export default PlayerPlateAppearanceFormatter;